import React, { useCallback } from 'react';
import { Box, Grid, Tooltip } from '@mui/material';
import { useFieldArray } from 'react-final-form-arrays';

import { Typography, InfoOutlinedIcon } from '@occc/ui-design-system';
import { TextField } from '@occc-app/design-system';
import { AddAnotherItem } from '@occc-app/routes/Audience/components/AddAnotherItem';
import { DeleteIcon } from '@occc-app/routes/Audience/components/WalletSteering/components/DeleteIcon';
import { parseNumber } from '@occc-app/lib/finalFormUtils';

import { GeneralSurveyQuestionType } from '../../types';
import { useCanEditFormFieldsContext } from '../../../../App/CanEditFormFieldsContext';

type QuestionAnswerOptionsProps = {
  questionType: GeneralSurveyQuestionType;
};

export const QuestionAnswerOptions = ({ questionType }: QuestionAnswerOptionsProps) => {
  const { isEditable } = useCanEditFormFieldsContext();
  const isDisabled = !isEditable;
  const { fields } = useFieldArray<string>('options');
  const handleAddItem = useCallback(() => fields.push(''), [fields]);
  const handleRemoveItem = useCallback((index: number) => fields.remove(index), [fields]);

  if (['SurveyQuestionSingleChoice', 'SurveyQuestionMultipleChoice'].includes(questionType)) {
    return (
      <Box mt={3}>
        <Typography variant='subtitle1'>Options</Typography>

        {fields.map((fieldArrayName, index) => (
          <React.Fragment key={fieldArrayName}>
            <Box display='flex' justifyContent='space-between'>
              <TextField name={fieldArrayName} label={`Option ${index + 1}`} margin='normal' />

              {!isDisabled && Boolean(fields.length ? fields.length > 2 : false) && (
                <DeleteIcon
                  sx={{ alignSelf: 'center', ml: 2 }}
                  onClick={() => {
                    handleRemoveItem(index);
                  }}
                />
              )}
            </Box>
          </React.Fragment>
        ))}
        {!isDisabled && Boolean(fields.length ? fields.length < 10 : false) && (
          <AddAnotherItem title='Add Another Option' onClick={handleAddItem} />
        )}
      </Box>
    );
  }

  return (
    <Box mt={3}>
      <Tooltip title='Minimum and maximum values in a Survey help ensure accurate data analysis by eliminating outliers and setting valid response range.'>
        <Box display='flex' flexDirection='row' mb={2}>
          <Typography variant='subtitle1'>Range</Typography>

          <Box display='flex' justifyContent='center' alignItems='center' ml={0.5}>
            <InfoOutlinedIcon sx={{ fontSize: '2rem', color: 'info.main' }} />
          </Box>
        </Box>
      </Tooltip>
      <Grid container spacing={3}>
        <Grid item xs={6}>
          <TextField
            name='rangeMin'
            label='Minimum'
            fieldProps={{
              parse: parseNumber,
            }}
          />
        </Grid>

        <Grid item xs={6}>
          <TextField
            name='rangeMax'
            label='Maximum'
            fieldProps={{
              parse: parseNumber,
            }}
          />
        </Grid>
      </Grid>
    </Box>
  );
};
